import { describeExperience, filledJobs, totalExperience } from "utils/prEnquiryForm";

/**
 * A PR enquiry as the admin screens read it: the record exactly as the API returns it (a list row
 * or the full read), plus what is derived from it. Work history is described and totalled here
 * with the same rules the public form shows the client, so both sides agree on the numbers.
 */
export const readEnquiry = (record, today = new Date()) => {
  if (!record) return {};
  const onshore = filledJobs(record.onshore || []);
  const offshore = filledJobs(record.offshore || []);
  return {
    ...record,
    onshoreJobs: onshore.map(describeExperience),
    offshoreJobs: offshore.map(describeExperience),
    onshoreExperienceTotal: totalExperience(onshore, today),
    offshoreExperienceTotal: totalExperience(offshore, today),
    consentToContact: record.consent ? "Yes" : "No",
  };
};

export const asList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === "") return [];
  return [String(value)];
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days from today until the visa expires (negative once expired), or null if unknown. */
export const daysUntil = (dateValue, today = new Date()) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  const startOfToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((startOfDate - startOfToday) / DAY_MS);
};

/** How urgent a visa expiry is, for colouring it: expired, under 3 months, under 6, or fine. */
export const visaUrgency = (days) => {
  if (days === null) return "unknown";
  if (days < 0) return "expired";
  if (days <= 90) return "urgent";
  if (days <= 180) return "soon";
  return "ok";
};

export const describeDays = (days) => {
  if (days === null) return "";
  if (days < 0) return `expired ${Math.abs(days)} day${days === -1 ? "" : "s"} ago`;
  if (days === 0) return "expires today";
  if (days < 60) return `${days} day${days === 1 ? "" : "s"} left`;
  return `${Math.round(days / 30)} months left`;
};

/** The sections of the profile page, in the order the client answered them. */
export const PR_PROFILE_SECTIONS = [
  {
    id: "about",
    title: "About",
    fields: [
      { key: "name", label: "Full name" },
      { key: "email", label: "Email" },
      { key: "number", label: "Phone" },
      { key: "dob", label: "Date of birth", date: true },
      { key: "nationality", label: "Nationality" },
      { key: "maritalStatus", label: "Relationship status" },
      { key: "inAustralia", label: "In Australia now" },
      { key: "currentCity", label: "Current city" },
    ],
  },
  {
    id: "visa",
    title: "Visa & study",
    fields: [
      { key: "visaStatus", label: "Current visa" },
      { key: "visaExpiry", label: "Visa valid until", date: true },
      { key: "currentCourse", label: "Course" },
      { key: "courseLevel", label: "Course level" },
      { key: "currentCourseProvider", label: "College / university" },
      { key: "courseEndDate", label: "Course ends", date: true },
      { key: "highestQualification", label: "Highest qualification" },
      { key: "twoYearsAustralianStudy", label: "2+ years Australian study" },
      { key: "regionalStudy", label: "Studied regionally" },
    ],
  },
  {
    id: "skills",
    title: "English & skills",
    fields: [
      { key: "englishTest", label: "English test" },
      { key: "englishScore", label: "Overall score" },
      { key: "readingScore", label: "Reading" },
      { key: "listeningScore", label: "Listening" },
      { key: "speakingScore", label: "Speaking" },
      { key: "writingScore", label: "Writing" },
      { key: "englishTestDate", label: "Test date", date: true },
      { key: "occupation", label: "Nominated occupation" },
      { key: "skillsAssessment", label: "Skills assessment" },
      { key: "professionalYear", label: "Professional Year" },
      { key: "naatiCcl", label: "NAATI CCL" },
    ],
  },
  {
    id: "goals",
    title: "PR goals",
    fields: [
      { key: "pathways", label: "Pathways of interest", chips: true },
      { key: "preferredStates", label: "Preferred states", chips: true },
      { key: "willingRegional", label: "Open to regional" },
      { key: "timeline", label: "Wants to apply" },
      { key: "partnerIncluded", label: "Partner included" },
      { key: "consentToContact", label: "Agreed to be contacted" },
    ],
  },
];

/** True when a value is worth showing (not blank, not an empty list). */
export const hasValue = (value) => asList(value).length > 0;
